import CheckboxTreeNodeDto from "../types/dtos/generic/CheckboxTreeNodeDto";

export interface FlatCheckboxTreeItem {
  value: string;
  label: string;
}

export interface CheckboxTreeSearchResult {
  filteredNodes: CheckboxTreeNodeDto[];
  visibleValues: string[];
  expandValues: string[];
}

/** Flatten tree nodes to value/label pairs (depth-first). */
export function flattenCheckboxTree(
  nodes: CheckboxTreeNodeDto[] | undefined
): FlatCheckboxTreeItem[] {
  if (!nodes?.length) return [];
  const result: FlatCheckboxTreeItem[] = [];
  function visit(n: CheckboxTreeNodeDto) {
    result.push({ value: n.value, label: n.label });
    n.children?.forEach(visit);
  }
  nodes.forEach(visit);
  return result;
}

/** Sort tree siblings alphabetically by label; order is preserved within each level. */
export function sortCheckboxTreeNodesByLabel(
  nodes: CheckboxTreeNodeDto[] | undefined
): CheckboxTreeNodeDto[] {
  if (!nodes?.length) return [];
  return [...nodes]
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    )
    .map((node) => ({
      ...node,
      children: node.children?.length
        ? sortCheckboxTreeNodesByLabel(node.children)
        : node.children,
    }));
}

/** All node values from a checkbox tree (depth-first). */
export function flattenCheckboxTreeToValues(
  nodes: CheckboxTreeNodeDto[] | undefined
): string[] {
  if (!nodes?.length) return [];
  const result: string[] = [];
  function visit(n: CheckboxTreeNodeDto) {
    result.push(n.value);
    n.children?.forEach(visit);
  }
  nodes.forEach(visit);
  return result;
}

/**
 * UI checked state from applied filter ids: no ids from API = "no filter" = all items ticked.
 */
export function checklistCheckedFromAppliedIds(
  appliedIds: number[] | null | undefined,
  allValues: string[]
): string[] {
  const ids = appliedIds ?? [];
  if (allValues.length === 0) return [];
  if (ids.length === 0) return [...allValues];
  return ids.map((x) => x.toString());
}

/**
 * Ids to send to the API: [] when nothing selected, all selected, or list is empty.
 * Otherwise the selected subset.
 */
export function submitIdsFromChecklistSelection(
  checked: string[],
  allValues: string[]
): number[] {
  if (allValues.length === 0) return [];
  if (checked.length === 0) return [];
  const checkedSet = new Set(checked.map(String));
  if (
    checkedSet.size === allValues.length &&
    allValues.every((v) => checkedSet.has(String(v)))
  ) {
    return [];
  }
  return checked.map((x) => parseInt(x, 10));
}

/** Node shape for react-checkbox-tree: omit `children` on leaves (empty [] counts as parent). */
export interface CheckboxTreeDisplayNode {
  value: string;
  label: string;
  children?: CheckboxTreeDisplayNode[];
}

/**
 * react-checkbox-tree treats any node with `children: []` as expandable.
 * Strip empty child arrays so leaf locations hide the expand chevron.
 */
export function normalizeCheckboxTreeNodesForDisplay(
  nodes: CheckboxTreeNodeDto[] | undefined
): CheckboxTreeDisplayNode[] {
  if (!nodes?.length) return [];

  return nodes.map((node) => {
    const normalizedChildren = normalizeCheckboxTreeNodesForDisplay(
      node.children?.length ? node.children : undefined
    );
    if (normalizedChildren.length > 0) {
      return {
        value: node.value,
        label: node.label,
        children: normalizedChildren,
      };
    }
    return { value: node.value, label: node.label };
  });
}

function compareLabels(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

/** Deep copy and sort a checkbox tree alphabetically at every level. */
export function sortCheckboxTreeNodes(
  nodes: CheckboxTreeNodeDto[] | undefined
): CheckboxTreeNodeDto[] {
  if (!nodes?.length) return [];

  return [...nodes]
    .sort((a, b) => compareLabels(a.label, b.label))
    .map((node) => ({
      ...node,
      children: sortCheckboxTreeNodes(node.children),
    }));
}

function pruneNode(
  node: CheckboxTreeNodeDto,
  queryLower: string
): CheckboxTreeNodeDto | null {
  const matchingChildren =
    node.children
      ?.map((child) => pruneNode(child, queryLower))
      .filter((child): child is CheckboxTreeNodeDto => child !== null) ?? [];

  if (
    node.label.toLowerCase().includes(queryLower) ||
    matchingChildren.length > 0
  ) {
    if (matchingChildren.length > 0) {
      return { ...node, children: matchingChildren };
    }
    return { value: node.value, label: node.label, children: [] };
  }

  return null;
}

function collectTreeValues(nodes: CheckboxTreeNodeDto[]): string[] {
  const result: string[] = [];
  function visit(n: CheckboxTreeNodeDto) {
    result.push(n.value);
    n.children?.forEach(visit);
  }
  nodes.forEach(visit);
  return result;
}

function collectExpandValues(nodes: CheckboxTreeNodeDto[]): string[] {
  const result: string[] = [];
  function visit(n: CheckboxTreeNodeDto) {
    if (n.children?.length) {
      result.push(n.value);
      n.children.forEach(visit);
    }
  }
  nodes.forEach(visit);
  return result;
}

/**
 * Prune a sorted tree to branches that match the search query (label or descendant).
 */
export function filterCheckboxTreeBySearch(
  nodes: CheckboxTreeNodeDto[] | undefined,
  query: string
): CheckboxTreeSearchResult {
  if (!nodes?.length) {
    return { filteredNodes: [], visibleValues: [], expandValues: [] };
  }

  const trimmed = query?.trim() ?? "";
  if (!trimmed) {
    const sorted = sortCheckboxTreeNodes(nodes);
    return {
      filteredNodes: sorted,
      visibleValues: collectTreeValues(sorted),
      expandValues: [],
    };
  }

  const queryLower = trimmed.toLowerCase();
  const filteredNodes = sortCheckboxTreeNodes(
    nodes
      .map((node) => pruneNode(node, queryLower))
      .filter((node): node is CheckboxTreeNodeDto => node !== null)
  );

  return {
    filteredNodes,
    visibleValues: collectTreeValues(filteredNodes),
    expandValues: collectExpandValues(filteredNodes),
  };
}

/** Locate a node by value in a checkbox tree (depth-first). */
export function findCheckboxTreeNodeByValue(
  nodes: CheckboxTreeNodeDto[] | undefined,
  value: string
): CheckboxTreeNodeDto | null {
  if (!nodes?.length) return null;

  for (const node of nodes) {
    if (String(node.value) === String(value)) return node;
    const found = findCheckboxTreeNodeByValue(node.children, value);
    if (found) return found;
  }

  return null;
}

/** All values in a subtree, including the root node. */
export function collectCheckboxTreeSubtreeValues(
  node: CheckboxTreeNodeDto
): string[] {
  const result: string[] = [node.value];
  node.children?.forEach((child) => {
    result.push(...collectCheckboxTreeSubtreeValues(child));
  });
  return result;
}

export interface CompressedCheckboxTreeSelection {
  id: string;
  label: string;
}

/**
 * Compress a flat selected-id list into maximal fully-selected subtrees.
 * A node is an effective root when it and all descendants are selected, and
 * its parent is not fully selected (or it is top-level).
 */
export function compressCheckboxTreeSelection(
  nodes: CheckboxTreeNodeDto[] | undefined,
  selectedIds: Array<string | number> | null | undefined
): CompressedCheckboxTreeSelection[] {
  if (!nodes?.length) return [];
  const selected = new Set((selectedIds ?? []).map(String));
  if (selected.size === 0) return [];

  const roots: CompressedCheckboxTreeSelection[] = [];

  function subtreeFullySelected(node: CheckboxTreeNodeDto): boolean {
    const values = collectCheckboxTreeSubtreeValues(node);
    return values.every((v) => selected.has(String(v)));
  }

  function visit(node: CheckboxTreeNodeDto, parentFullySelected: boolean) {
    const fully = subtreeFullySelected(node);
    if (fully && !parentFullySelected) {
      roots.push({ id: String(node.value), label: node.label });
      return;
    }
    node.children?.forEach((child) => visit(child, fully));
  }

  nodes.forEach((node) => visit(node, false));
  return roots;
}

/** Build id → label map from a checkbox tree (depth-first). */
export function checkboxTreeLabelMap(
  nodes: CheckboxTreeNodeDto[] | undefined
): Map<number, string> {
  const map = new Map<number, string>();
  if (!nodes?.length) return map;
  function visit(n: CheckboxTreeNodeDto) {
    const id = parseInt(String(n.value), 10);
    if (!Number.isNaN(id)) map.set(id, n.label);
    n.children?.forEach(visit);
  }
  nodes.forEach(visit);
  return map;
}

/** True when the node has no children in the pruned search tree. */
export function isLeafInFilteredTree(
  filteredNodes: CheckboxTreeNodeDto[],
  value: string
): boolean {
  const node = findCheckboxTreeNodeByValue(filteredNodes, value);
  if (!node) return false;
  return !node.children?.length;
}

export interface SearchAwareTreeCheckChangeParams {
  prevChecked: string[];
  libraryChecked: string[];
  targetValue: string;
  fullTree: CheckboxTreeNodeDto[];
  filteredNodes: CheckboxTreeNodeDto[];
  visibleValues: string[];
  isChecking: boolean;
}

/**
 * Merge react-checkbox-tree output with full-tree state during search.
 * Leaf nodes in the pruned view also select/deselect their full real subtree.
 */
export function applySearchAwareTreeCheckChange({
  prevChecked,
  libraryChecked,
  targetValue,
  fullTree,
  filteredNodes,
  visibleValues,
  isChecking,
}: SearchAwareTreeCheckChangeParams): string[] {
  const visibleSet = new Set(visibleValues.map(String));
  const offScreenChecked = prevChecked.filter(
    (id) => !visibleSet.has(String(id))
  );
  const merged = Array.from(
    new Set([...offScreenChecked, ...libraryChecked.map(String)])
  );

  if (!isLeafInFilteredTree(filteredNodes, targetValue)) {
    return merged;
  }

  const fullNode = findCheckboxTreeNodeByValue(fullTree, targetValue);
  if (!fullNode) return merged;

  const subtreeSet = new Set(
    collectCheckboxTreeSubtreeValues(fullNode).map(String)
  );

  if (isChecking) {
    return Array.from(new Set([...merged, ...Array.from(subtreeSet)]));
  }

  return merged.filter((id) => !subtreeSet.has(String(id)));
}
